import { Card, Modal, Popconfirm, Popover, Space, Spin, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import ListItem from "@mui/material/ListItem";
import Typography from "@mui/material/Typography";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ServiceList from "../servicelist/ServiceList";
import "./servicessettings.css";
import { useRecoilValue } from "recoil";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../utils/configurationconstants";
import CreateNewService from "../createnewservice/CreateNewService";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import NoDataMessageComponent from "../../../components/generic/nodatamessage/NoDataMessageComponent";
import SmartIcon from "../../../components/generic/genericIcon/SmartIcon";

const ServicesSettings = ({
	masterServices,
	receivedSelectedServices,
	isServicesLoading,
	isSelectedServicesLoading,
	addServices,
	getServices,
	deleteService,
	currentAddedService,
	editServices,
	serviceToBeEdited,
	isOrganizationProfilePage,
}) => {
	const [isModalVisible, setModalVisible] = useState(false);
	const [selectedServices, setSelectedServices] = useState([]);
	const [toggledServices, setToggledServices] = useState({});
	const [services, setServices] = useState([]);
	const [editServiceItem, setEditServiceItem] = useState({});
	const [isEditServicesLoading, setIsEditServicesLoading] = useState(false);

	const [addServiceOption, setAddServiceOption] = useState("");

	// Feature access settings
	const configuration = useRecoilValue(currentConfiguration);
	const [isAddServicesActionAllowed, setIsAddServicesActionAllowed] = useState(false);
	const [isDeleteServicesActionAllowed, setIsDeleteServicesActionAllowed] = useState(false);
	const [isEditServicesActionAllowed, setIsEditServicesActionAllowed] = useState(false);
	const [isCreateNewServicesActionAllowed, setIsCreateNewServicesActionAllowed] = useState(false);
	const [isCoordinatorCreateNewServiceConfiguration, setIsCoordinatorCreateNewServiceConfiguration] = useState(false);

	useEffect(() => {
		const configurationAddServicesAction = configuration[ConfigurationConstants.FEATUREACCESS_PROFILE_SERVICES_ADD_SERVICES];
		setIsAddServicesActionAllowed(configurationAddServicesAction?.visibility);

		const configurationDeleteServicesAction = configuration[ConfigurationConstants.FEATUREACCESS_PROFILE_SERVICES_DELETE_SERVICES];
		setIsDeleteServicesActionAllowed(configurationDeleteServicesAction?.visibility);

		const configurationEditServicesAction = configuration[ConfigurationConstants.FEATUREACCESS_PROFILE_SERVICES_EDIT_SERVICES];
		setIsEditServicesActionAllowed(configurationEditServicesAction?.visibility);

		const configurationCreateNewServicesAction = configuration[ConfigurationConstants.FEATUREACCESS_PROFILE_SERVICES_CREATE_NEW_SERVICE];
		setIsCreateNewServicesActionAllowed(configurationCreateNewServicesAction?.visibility);

		const coordinatorCreateNewServiceConfiguration = configuration[ConfigurationConstants.FEATUREACCESS_PROFILE_SERVICES_CREATE_NEW_SERVICE_COORDINATOR_ACCESS];
		setIsCoordinatorCreateNewServiceConfiguration(coordinatorCreateNewServiceConfiguration?.visibility);
	}, [configuration]);

	useEffect(() => {
		setSelectedServices(receivedSelectedServices);
		setServices(masterServices);
		setEditServiceItem(serviceToBeEdited);
	}, [receivedSelectedServices, masterServices, serviceToBeEdited]);

	/**
	 * When a service is added from The popup (Service List) for a volunteer. It gets directly added inside
	 * the selectedService state. But if we see closely it only has the title and SkillKey. Now, if we try to delete it
	 * as it doesn't has the skillIdentifier it is imposible to delete that skill. To resolve this 'currentAddedService'
	 * is used. From the VolunterrProfilePage the currentAddedService is set once it is added and that is updated
	 * in the selecetdServices state.
	 */
	useEffect(() => {
		if (currentAddedService) {
			// To update the logic in JavaScript way.
			const selectedServicesArray = [];
			for (let index = 0; index < selectedServices?.length; index++) {
				if (selectedServices[index]?.title == currentAddedService.title) {
					selectedServicesArray.push(currentAddedService);
				} else {
					selectedServicesArray[index] = selectedServices[index];
				}
			}
			setSelectedServices(selectedServicesArray);
		}
	}, [currentAddedService]);

	const openModal = (option) => {
		if (option === "Existing Services") {
			for (let i in selectedServices) {
				setToggledServices((item) => ({
					...item,
					[selectedServices[i].title]: true,
				}));
			}
			getServices();
		}
		setAddServiceOption(option);
		setModalVisible(true);
	};

	const closeModal = () => {
		setModalVisible(false);
	};

	const handleServiceToggle = (service, serviceStatus) => {
		setToggledServices((prevToggledServices) => ({
			...prevToggledServices,
			[service.title]: !prevToggledServices[service.title],
		}));
		setSelectedServices((prevServices) => (toggledServices[service.title] ? prevServices?.filter((prevService) => prevService.title !== service.title) : [...prevServices, service]));
		const servicesList = toggledServices[service.title] ? selectedServices?.filter((prevService) => prevService.title !== service.title) : [...selectedServices, service];
		if (serviceStatus) {
			const serviceToBeDeleted = selectedServices?.filter((item) => item?.title === service.title);
			deleteService(servicesList, serviceToBeDeleted[0], "delete service");
		} else addServices(servicesList, service);
	};

	const removeService = async (service) => {
		setToggledServices((prevToggledServices) => {
			const updatedToggledServices = { ...prevToggledServices };
			delete updatedToggledServices[service.title];
			return updatedToggledServices;
		});
		setSelectedServices((prevServices) => prevServices?.filter((prevService) => prevService.title !== service.title));
		const servicesList = selectedServices.filter((prevService) => prevService.title !== service.title);
		deleteService(servicesList, service, "delete service");
	};

	const editService = async (service) => {
		setIsEditServicesLoading(true);
		setToggledServices((prevToggledServices) => {
			const editedToggledServices = { ...prevToggledServices };
			delete editedToggledServices[service.title];
			return editedToggledServices;
		});
		const obj = await editServices(service);
		setIsEditServicesLoading(false);
		setEditServiceItem(obj);
		openModal("Edit Service");
	};

	const content = (
		<div className="services-popover-content">
			<Typography className="services-popover-button" onClick={() => openModal("Existing Services")}>
				Add from Existing Services
			</Typography>
			{isOrganizationProfilePage && isCreateNewServicesActionAllowed && (
				<Typography className="services-popover-button" onClick={() => openModal("Create New Service")}>
					Create New Service
				</Typography>
			)}
		</div>
	);

	const handleAddServicesBtn = () => {
		openModal("Existing Services")
	}

	const sortData = (dataList) => dataList?.sort((optionA, optionB) =>
		(optionA?.title || "").toLowerCase().localeCompare((optionB?.title || "").toLowerCase())
	);
	const sortedSelectedServices = sortData(selectedServices);

	return (
		<div className="services-main-div">
				<Card
					className="services-card"
					title={
						<>
							<div className="services-card-title">
								<strong className="services-card-title-font">Services</strong>
								{isAddServicesActionAllowed &&
									(isCoordinatorCreateNewServiceConfiguration ? (
										<Popover placement="bottom" content={content} overlayClassName="services-popover">
											<>
												<PrimaryButton buttonText="Manage Services"/>
											</>
										</Popover>
									) : (
										<PrimaryButton buttonText="Add Services" handleOnClick={handleAddServicesBtn}/>
									))}
							</div>
						</>
					}
				>
					{isSelectedServicesLoading ? (
						<Spin className="spinnerCenter" />
					) : selectedServices !== undefined && selectedServices?.length !== 0 ? (
						sortedSelectedServices?.map((service) => (
									<ListItem className="services-listitem" key={service.title}>
								<Typography className="services-listitem-font">{service.title}</Typography>
								<ListItemAvatar className="services-list-item-action">
									<Space size={"middle"}>
										{isEditServicesActionAllowed && editServices !== undefined && (
											<Tooltip title="Edit" placement="bottom">
												<>
													<SmartIcon iconKey={"edit"} width="20" height="20" handleIconClick={() => editService(service)} />
												</>
											</Tooltip>

										)}
										{isDeleteServicesActionAllowed && (
											<Tooltip title="Remove" placement="bottom">
												<>
													<Popconfirm
														title="Are you sure you want to remove this service?"
														placement="top"
														okText="Confirm"
														cancelText="Cancel"
														onConfirm={() => removeService(service)}
														overlayClassName="article-delete-popconfirm"
													>
														<span className="saved-cards-action-wrap">
															<SmartIcon iconKey="delete" width="20" height="20" />
														</span>
													</Popconfirm>
												</>
											</Tooltip>
										)}
									</Space>
								</ListItemAvatar>
							</ListItem>
						))
					) : (
						<NoDataMessageComponent text="No services selected" />
					)}
				</Card>
				{isModalVisible && (
					<Modal
						bodyStyle={{ overflowY: "auto", marginTop: "3px" }}
						open={isModalVisible}
						onOk={closeModal}
						onCancel={closeModal}
						footer={null}
						centered
						title={addServiceOption}
					>
						{addServiceOption === "Existing Services" && (
							<ServiceList onToggle={handleServiceToggle} toggled={toggledServices} data={services} isLoading={isServicesLoading} currentAddedService={currentAddedService} />
						)}
						{addServiceOption === "Edit Service" && (
							<CreateNewService
								initialValues={editServiceItem}
								addService={addServices}
								selectedServices={selectedServices}
								setSelectedServices={setSelectedServices}
								closeModal={closeModal}
								isEditServicesLoading={isEditServicesLoading}
								addServiceOption={addServiceOption}
							/>
						)}
						{addServiceOption === "Create New Service" && (
							<CreateNewService
								addService={addServices}
								selectedServices={selectedServices}
								setSelectedServices={setSelectedServices}
								closeModal={closeModal}
								addServiceOption={addServiceOption}
							/>
						)}
					</Modal>
				)}
		</div>
	);
};

export default ServicesSettings;
